import cn from 'classnames';
import React from 'react';
import Image from 'next/image';

import type { ColourSwatchModel } from './useColourSwatchModel';
import styles from './ColourSwatch.module.scss';

export interface ColourSwatchViewProps extends ColourSwatchModel {
  wrapperClassName: string;
  buttonClassName: string;
  imageWidth: number;
  imageHeight: number;
  tabIndex?: number;
}

/**
 * Presentational shell: layout + image/hex + deal dot. Variant-specific classes
 * and dimensions are supplied by ColourSwatchPdp / ColourSwatchCard.
 */
const ColourSwatchView: React.FC<ColourSwatchViewProps> = ({
  colour,
  displayColourName,
  hasImage,
  hasHex,
  isNoData,
  disabledLabel,
  onSelect,
  onPreload,
  wrapperClassName,
  buttonClassName,
  imageWidth,
  imageHeight,
  tabIndex,
}) => (
  <div className={wrapperClassName}>
    <button
      type="button"
      className={buttonClassName}
      role="radio"
      aria-checked={colour.isSelected}
      aria-label={displayColourName}
      title={displayColourName}
      tabIndex={tabIndex}
      onClick={onSelect}
      onMouseEnter={onPreload}
    >
      <span className={styles.screenReader}>
        {displayColourName}
        {disabledLabel}
      </span>
      {hasImage && colour.swatchImage ? (
        <span className={styles.inner}>
          <Image
            width={imageWidth}
            height={imageHeight}
            src={colour.swatchImage}
            className={styles.imageInner}
            alt=""
            quality={60}
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </span>
      ) : (
        <span
          className={styles.inner}
          style={{
            backgroundColor: isNoData
              ? 'var(--color-white)'
              : hasHex
              ? colour.hexColours?.[0]
              : undefined,
          }}
        />
      )}
    </button>
    <span
      className={cn(styles.dealDot, {
        [styles.dealDotActive]: colour.isDeal,
      })}
      aria-hidden="true"
    />
  </div>
);

export default ColourSwatchView;
